/** The 15-minute voice (docs/alert_sounds.md): during the market the browser says the time at the close
 * of every 15-minute candle, 09:30, 09:45 ... 15:30 IST, on trading days. Pure: the clock and the
 * speech are the caller's. IST is worked out from the instant, never from the computer's own zone.
 */

const IST_OFFSET_MS = 330 * 60_000;
const OPEN_MINUTE = 9 * 60 + 30; // the first 15-minute candle (09:15) closes at 09:30
const CLOSE_MINUTE = 15 * 60 + 30; // the last one closes with the market
const STEP = 15;
/** A call is still made this many seconds late: a hidden tab's timer may run only once a minute. */
const LATE_SECONDS = 75;

export interface Slot {
  /** "2026-11-04 09:30": one call each. */
  key: string;
  hour: number;
  minute: number;
}

/** The IST date and time of an instant, as numbers. */
function ist(ms: number): { date: string; minutes: number; seconds: number; weekday: number } {
  const shifted = new Date(ms + IST_OFFSET_MS);
  return {
    date: shifted.toISOString().slice(0, 10),
    minutes: shifted.getUTCHours() * 60 + shifted.getUTCMinutes(),
    seconds: shifted.getUTCSeconds(),
    weekday: shifted.getUTCDay(),
  };
}

/** The date in IST of an instant, "YYYY-MM-DD". */
export const istDate = (ms: number): string => ist(ms).date;

/**
 * The 15-minute close that is due now, or null: an instant within a minute and a quarter after a close
 * from 09:30 to 15:30 IST on a weekday. The caller says whether the day is a trading day.
 */
export function dueSlot(ms: number): Slot | null {
  const { date, minutes, seconds, weekday } = ist(ms);
  const sinceClose = (minutes % STEP) * 60 + seconds;
  const close = minutes - (minutes % STEP);
  if (weekday === 0 || weekday === 6 || sinceClose >= LATE_SECONDS) return null;
  if (close < OPEN_MINUTE || close > CLOSE_MINUTE) return null;
  const hour = Math.floor(close / 60);
  const minute = close % 60;
  return {
    key: `${date} ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
    hour,
    minute,
  };
}

const HOURS = [
  "twelve",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
];
const MINUTES: Record<number, string> = {
  0: "o'clock",
  15: "fifteen",
  30: "thirty",
  45: "forty five",
};

/** What is said for a close: "Nine thirty", "Ten o'clock", "Three fifteen". */
export function spokenTime(hour: number, minute: number): string {
  const word = HOURS[hour % 12] ?? "";
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} ${MINUTES[minute] ?? String(minute)}`;
}
